import type { AuthTokens } from '../types/auth';

function pickString(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return undefined;
}

const ACCESS_KEYS = [
  'accessToken',
  'access_token',
  'token',
  'jwt',
  'access',
  'bearerToken',
  'bearer_token',
];

const REFRESH_KEYS = ['refreshToken', 'refresh_token', 'refresh'];

function tryExtractFromObject(o: Record<string, unknown>): AuthTokens | null {
  const accessToken = pickString(o, ACCESS_KEYS);
  const refreshToken = pickString(o, REFRESH_KEYS);
  if (accessToken && refreshToken) return { accessToken, refreshToken };
  return null;
}

/**
 * Accepts camelCase/snake_case tokens at the root or nested (e.g. `tokens`, `data`, `session`, `auth`).
 */
export function parseAuthTokensPayload(data: unknown): AuthTokens | null {
  if (!data || typeof data !== 'object') return null;

  const seen = new WeakSet<object>();

  function walk(value: unknown, depth: number): AuthTokens | null {
    if (depth > 6 || !value || typeof value !== 'object') return null;
    if (seen.has(value as object)) return null;
    seen.add(value as object);

    const o = value as Record<string, unknown>;
    const direct = tryExtractFromObject(o);
    if (direct) return direct;

    for (const v of Object.values(o)) {
      if (v != null && typeof v === 'object') {
        const nested = walk(v, depth + 1);
        if (nested) return nested;
      }
    }
    return null;
  }

  return walk(data, 0);
}

/** Access JWT lifetime in seconds (root or nested `data`). */
export function parseExpiresIn(data: unknown): number | undefined {
  if (!data || typeof data !== 'object') return undefined;

  function read(o: Record<string, unknown>): number | undefined {
    const v = o.expiresIn ?? o.expires_in;
    if (typeof v === 'number' && v > 0) return v;
    if (typeof v === 'string') {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return undefined;
  }

  const root = data as Record<string, unknown>;
  const direct = read(root);
  if (direct) return direct;

  const nested = root.data;
  if (nested && typeof nested === 'object') {
    return read(nested as Record<string, unknown>);
  }
  return undefined;
}
